import { useCallback } from "react"

import type { DraftAdjustment } from "~/components/demand-2/demandDraftState"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { useChannels } from "~/hooks/useChannels"
import { api } from "~/utils/api"
import {
  CHANNEL_ALL,
  channelNameOf,
  MARKET_ALL,
  marketNameOf,
  WAREHOUSE_ALL,
  WHOLE_VARIANT_SCOPE,
} from "~/utils/demandScope"
import { levelLabel, UNCATEGORIZED } from "~/utils/demandHierarchy"
import { isCubeAxis, type StrategyFilters } from "~/utils/demandStrategyScope"

/** The axes as a row carries them. Null reads as the "all" sentinel: rows predating an axis carry none. */
export type ScopeAxes = {
  channel: string | null
  warehouseId: string | null
  marketId: string | null
}

/**
 * Scoped slices as names ("Copenhagen · Wholesale · Germany"), or `null` for the whole variant.
 * Markets go through the shared `marketNameOf`, as the publish trail does.
 */
export function useScopeAxesLabel(): (axes: ScopeAxes) => string | null {
  const { collectionIds } = useDemand2Scope()
  const warehousesQ = api.warehouse.get.useQuery()
  const channelsQ = useChannels()
  const dimsQ = api.demand2.getDimensions.useQuery(
    { collectionIds },
    { enabled: collectionIds.length > 0 },
  )

  return useCallback(
    (axes: ScopeAxes): string | null => {
      const warehouseId = axes.warehouseId ?? WAREHOUSE_ALL
      const channel = axes.channel ?? CHANNEL_ALL
      const marketId = axes.marketId ?? MARKET_ALL
      const parts = [
        warehouseId !== WAREHOUSE_ALL
          ? (warehousesQ.data?.find((w) => w.id === warehouseId)?.name ?? warehouseId)
          : null,
        channel !== CHANNEL_ALL ? channelNameOf(channelsQ.data, channel) : null,
        marketId !== MARKET_ALL ? marketNameOf(dimsQ.data?.markets ?? [], marketId) : null,
      ].filter(Boolean)
      return parts.length ? parts.join(" · ") : null
    },
    [warehousesQ.data, channelsQ.data, dimsQ.data],
  )
}

/**
 * The same label for a draft adjustment. Reads `scope`, not `level`, which collapses a composite
 * scope to its most-specific axis.
 */
export function useScopeLabel(): (a: DraftAdjustment) => string | null {
  const label = useScopeAxesLabel()
  const filtersLabel = useStrategyFiltersLabel()
  return useCallback(
    (a: DraftAdjustment) => {
      if (a.filters && Object.keys(a.filters).length > 0) return filtersLabel(a.filters)
      return a.scope ? label(a.scope) : null
    },
    [label, filtersLabel],
  )
}

export const useScopeValueLabel = () => {
  const axesLabel = useScopeAxesLabel()
  return useCallback(
    (level: string, value: string): string =>
      isCubeAxis(level)
        ? (axesLabel({ ...WHOLE_VARIANT_SCOPE, [level]: value }) ?? value)
        : value || UNCATEGORIZED,
    [axesLabel],
  )
}

const useStrategyFiltersLabel = () => {
  const valueLabel = useScopeValueLabel()
  return useCallback(
    (filters: StrategyFilters): string | null => {
      const parts = Object.entries(filters).map(
        ([level, values]) =>
          `${levelLabel(level)}: ${values.map((v) => valueLabel(level, v)).join(", ")}`,
      )
      return parts.length > 0 ? parts.join(" · ") : null
    },
    [valueLabel],
  )
}
